/**
 * LifecycleSelect — inline pipeline-status changer. Renders as a styled select
 * that PATCHes /api/crm/contacts/:id { lifecycle_status } itself (optimistic,
 * reverts on failure). Used on the contact detail header; safe inside clickable
 * rows (stops propagation).
 *
 * Moving a lead to a terminal-negative disposition (Not Interested / Unreachable
 * / Screen Failed / Not Eligible) first opens DispositionReasonModal to collect a
 * REQUIRED reason, which rides the PATCH and feeds the Dispositions report.
 *
 * Moving a lead into a STUDY-ATTRIBUTED stage (Screened / Randomized — and the
 * study section of the Screen Failed reason modal) first confirms WHICH study
 * the outcome belongs to; the chosen study rides the same PATCH as
 * primary_study_id so the reporting ledger freezes the transition under the
 * right study (pivoted leads must never be counted under the study they came
 * in for). The backend rejects these moves without it.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { LIFECYCLES, FINAL_DISPOSITIONS, SCHEDULE_GATE_STAGES, STUDY_ATTRIBUTED_STAGES, lifecycleMeta } from '../lib';
import { ScheduleReadiness } from '../types';
import DispositionReasonModal from './DispositionReasonModal';
import StageStudyConfirmModal from './StageStudyConfirmModal';
import ScheduleGateModal from './ScheduleGateModal';
import RandomizedConflictModal from './RandomizedConflictModal';

interface Props {
  contactId: string;
  value: string | null | undefined;
  /** studyId is set when the change carried a confirmed study (study-attributed
   *  stages) — the same PATCH re-pointed the considering study to it. */
  onChanged?: (newStatus: string, studyId?: string) => void;
  className?: string;
  /** Scheduling readiness for the lead's current study — drives the "requirements
   *  not met" warning when moving into a scheduling stage. */
  scheduleReadiness?: ScheduleReadiness | null;
  /** The lead's current considering study — preselects the study confirmation
   *  when moving into a study-attributed stage. */
  primaryStudyId?: string | null;
  /**
   * Option source. Defaults to the CONTACT pipeline (LIFECYCLES), which by
   * definition excludes the membership-only terminals — those exist only on a
   * study-list row and must never be offered as a contact lifecycle_status. The
   * Studies card passes MEMBERSHIP_STATUSES so a per-study row CAN be marked
   * completed / discontinued (multi-study lead lists, plan §6.1/§6.2).
   */
  statuses?: string[];
  /**
   * Write override. When set, the select calls this instead of PATCHing
   * /contacts/:id — the Studies card points it at
   * PATCH /contacts/:id/studies/:studyId so one component, and one visual
   * language, serves both the pipeline stage and a per-study status. A throw
   * reverts the optimistic value exactly like a failed PATCH.
   *
   * The study-confirmation and scheduling-gate prompts are skipped in this
   * mode: the study is already known (it is the row), and the gate is a
   * property of the lead's scheduling, not of a second study's list.
   */
  commit?: (next: string, extra: Record<string, string | boolean | string[]>) => Promise<void>;
}

const lifecycleIndex = (v: string) => LIFECYCLES.findIndex((l) => l.value === v);

export default function LifecycleSelect({
  contactId, value, onChanged, className = '', scheduleReadiness, primaryStudyId,
  statuses, commit: commitOverride,
}: Props) {
  const [current, setCurrent] = useState((value || 'new').toLowerCase());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  // The terminal status awaiting a reason (modal open) — never applied until the
  // recruiter confirms a reason; cancelling leaves `current` untouched so the
  // controlled select snaps back to the prior status.
  const [pending, setPending] = useState<string | null>(null);
  // The study-attributed positive status awaiting a study confirmation.
  const [pendingStudy, setPendingStudy] = useState<string | null>(null);
  // The scheduling status awaiting an override acknowledgement (gate modal open).
  const [pendingSchedule, setPendingSchedule] = useState<string | null>(null);
  // A 409 randomized_conflict, with the exact request that tripped it stashed
  // so confirming can re-send it verbatim plus the acknowledge flag
  // (multi-study lead lists, plan §6.4).
  const [conflict, setConflict] = useState<{
    next: string;
    extra: Record<string, string | boolean | string[]>;
    randomizedStudyTitle?: string | null;
    randomizedAt?: string | null;
  } | null>(null);

  // Whether a change of OURS is in flight or awaiting a modal — mirrored into a
  // ref so the external-sync effect below can depend on `value` alone. Depending
  // on these directly would re-run the effect the moment they clear, at which
  // point `value` is still the pre-save status on parents that refetch
  // asynchronously (the Studies card) — snapping the badge back to the old
  // status right after a successful save.
  const busyRef = useRef(false);
  busyRef.current = saving
    || pending !== null || pendingStudy !== null || pendingSchedule !== null || conflict !== null;

  // Adopt an EXTERNAL change to the lead's status: a parent refetch after a
  // per-study write moved the focus study, a realtime update, or the same
  // mounted select being handed a different lead. Skipped while busy: our own
  // optimistic value is authoritative until its request settles, and a pending
  // reason / study-confirm / schedule-gate / conflict modal is sitting on a
  // status the recruiter has not confirmed yet — resetting `current` under
  // either would silently discard their in-progress change.
  useEffect(() => {
    if (busyRef.current) return;
    setCurrent((value || 'new').toLowerCase());
  }, [value]);

  const meta = lifecycleMeta(current);
  const options = statuses
    ? statuses.map((v) => lifecycleMeta(v))
    : LIFECYCLES;
  const isKnown = options.some((l) => l.value === current);

  // Apply a status change (optionally with a captured disposition reason, a
  // confirmed study, or a scheduling-gate override note).
  const commit = async (next: string, extra: Record<string, string | boolean | string[]> = {}) => {
    const prev = current;
    setCurrent(next);
    setSaving(true);
    setError(false);
    try {
      if (commitOverride) {
        await commitOverride(next, extra);
      } else {
        await api.patch(`/api/crm/contacts/${contactId}`, { lifecycle_status: next, ...extra });
      }
      onChanged?.(next, typeof extra.primary_study_id === 'string' ? extra.primary_study_id : undefined);
    } catch (e: any) {
      setCurrent(prev);
      // The lead is randomized in another study — offer the acknowledgement
      // instead of failing. Never auto-retried: it is the recruiter's call.
      const data = e?.response?.data;
      if (e?.response?.status === 409 && data?.reason === 'randomized_conflict') {
        setConflict({
          next,
          extra,
          randomizedStudyTitle: data.randomized_study?.title ?? null,
          randomizedAt: data.randomized_at ?? null,
        });
      } else {
        setError(true);
        setTimeout(() => setError(false), 3000);
      }
    } finally {
      setSaving(false);
    }
  };

  const change = (next: string) => {
    if (next === current || saving) return;
    // Negative dispositions need a reason first — defer the PATCH to the modal.
    // (Screen Failed also collects the study there — it's study-attributed.)
    if (FINAL_DISPOSITIONS.includes(next)) {
      setPending(next);
      return;
    }
    // Study-attributed positives (Screened / Randomized) confirm the study
    // first — but not in per-study mode, where the study IS the row.
    if (!commitOverride && STUDY_ATTRIBUTED_STAGES.includes(next)) {
      setPendingStudy(next);
      return;
    }
    // Scheduling gate: moving FORWARD into a scheduling stage before the study's
    // required milestones are met warns the recruiter (they can still proceed).
    if (
      !commitOverride &&
      SCHEDULE_GATE_STAGES.includes(next) &&
      lifecycleIndex(next) > lifecycleIndex(current) &&
      scheduleReadiness?.configured &&
      !scheduleReadiness.allMet
    ) {
      setPendingSchedule(next);
      return;
    }
    void commit(next);
  };

  return (
    <span className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <select
        value={current}
        disabled={saving}
        onChange={(e) => change(e.target.value)}
        title={error ? 'Could not update status — try again' : 'Change lead status'}
        aria-label="Lead status"
        className={`min-h-[44px] cursor-pointer appearance-none rounded-full border-0 py-1 pl-4 pr-7 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-60 sm:min-h-0 ${meta.badge} ${
          error ? 'ring-2 ring-red-400' : ''
        } ${className}`}
      >
        {options.map((l) => (
          <option key={l.value} value={l.value}>{l.label}</option>
        ))}
        {!isKnown && <option value={current}>{meta.label}</option>}
      </select>
      <svg
        className="pointer-events-none absolute right-2 top-1/2 h-3 w-3 -translate-y-1/2 opacity-60"
        viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
      >
        <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {pending && (
        <DispositionReasonModal
          status={pending}
          askStudy={!commitOverride && STUDY_ATTRIBUTED_STAGES.includes(pending)}
          defaultStudyId={primaryStudyId}
          onCancel={() => setPending(null)}
          onConfirm={(reasonCode, note, studyId, criterionId, interestIds) => {
            const next = pending;
            setPending(null);
            const extra: Record<string, string | boolean | string[]> = { reason_code: reasonCode };
            if (note) extra.disposition_note = note;
            if (studyId) extra.primary_study_id = studyId;
            // Optional — names the prescreen question they failed, which is
            // what carries the DQ into the by-criterion report.
            if (criterionId) extra.criterion_id = criterionId;
            // Optional — future-study lists to keep them on (owner 2026-08-20).
            if (interestIds?.length) extra.interest_category_ids = interestIds;
            void commit(next, extra);
          }}
        />
      )}

      {pendingStudy && (
        <StageStudyConfirmModal
          status={pendingStudy}
          defaultStudyId={primaryStudyId}
          onCancel={() => setPendingStudy(null)}
          onConfirm={(studyId) => {
            const next = pendingStudy;
            setPendingStudy(null);
            void commit(next, studyId ? { primary_study_id: studyId } : {});
          }}
        />
      )}

      {conflict && (
        <RandomizedConflictModal
          randomizedStudyTitle={conflict.randomizedStudyTitle}
          randomizedAt={conflict.randomizedAt}
          onCancel={() => setConflict(null)}
          onConfirm={() => {
            const pendingReq = conflict;
            setConflict(null);
            void commit(pendingReq.next, {
              ...pendingReq.extra,
              randomized_conflict_acknowledged: true,
            });
          }}
        />
      )}

      {pendingSchedule && (
        <ScheduleGateModal
          targetStatus={pendingSchedule}
          unmet={scheduleReadiness?.unmet || []}
          onCancel={() => setPendingSchedule(null)}
          onConfirm={(note) => {
            const next = pendingSchedule;
            setPendingSchedule(null);
            void commit(next, note
              ? { schedule_gate_override: true, schedule_gate_note: note }
              : { schedule_gate_override: true });
          }}
        />
      )}
    </span>
  );
}
